/**
 * Client side of the pre-reserved bill-number block (README.md Phase 21,
 * `apps/api/src/services/core/id-generator.service.ts`'s `issueIdBlock`).
 * Reserved while online — via `POST /bills/reserve-block` — well before a
 * terminal might need it, so a sale started with zero connectivity still
 * gets a real, collision-free number to print immediately. The formatted
 * string is built with the exact same `PREFIX + shopCode + '-' + padded
 * value` rule `mintId` uses server-side (packages/shared's `ID_SERIES_PREFIX`),
 * so an offline-minted number is byte-identical to what the server would
 * have minted for the same value.
 */
import { api } from './api';
import { getIdBlock, putIdBlock, type IdBlockRecord } from './offline-db';

const BLOCK_SIZE = 20;
/** Reserve a fresh block once the held one drops below this many unused numbers. */
const LOW_WATER_MARK = 3;

interface ReserveBlockResponse {
  blockId: string;
  fromValue: number;
  toValue: number;
  shopCode: string;
  prefix: string;
}

function format(prefix: string, shopCode: string, value: number): string {
  const shopPart = `${prefix}${shopCode.replace(/^S/i, '')}`;
  return `${shopPart}-${String(value).padStart(6, '0')}`;
}

/** Call while online — reserves a new block if none is held or the current one is running low. No-ops (throws) if the request fails; callers should treat this as best-effort. */
export async function ensureIdBlock(shopId: string): Promise<void> {
  const existing = await getIdBlock(shopId);
  if (existing && existing.toValue - existing.nextValue + 1 > LOW_WATER_MARK) return;

  const res = await api.post<ReserveBlockResponse>('/bills/reserve-block', { shopId, size: BLOCK_SIZE });
  const record: IdBlockRecord = {
    shopId,
    blockId: res.blockId,
    prefix: res.prefix,
    shopCode: res.shopCode,
    fromValue: res.fromValue,
    toValue: res.toValue,
    nextValue: res.fromValue,
  };
  await putIdBlock(record);
}

/** How many numbers this terminal still has on hand for this shop, offline. */
export async function remainingInBlock(shopId: string): Promise<number> {
  const block = await getIdBlock(shopId);
  if (!block) return 0;
  return Math.max(0, block.toValue - block.nextValue + 1);
}

/**
 * Consumes and returns the next reserved number, or `null` if the terminal
 * is holding none — the caller (offline-bill.ts) must refuse to start a
 * fresh cold-start sale in that case rather than sell with no number to
 * print, since the whole point of the block is a receipt is never "TBD".
 */
export async function takeNextBillNo(shopId: string): Promise<string | null> {
  const block = await getIdBlock(shopId);
  if (!block || block.nextValue > block.toValue) return null;
  const value = block.nextValue;
  await putIdBlock({ ...block, nextValue: value + 1 });
  return format(block.prefix, block.shopCode, value);
}
